import React from "react";
import {
  Box,
  Drawer,
  Typography,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Switch,
  Button,
  Divider,
  useMediaQuery,
} from "@mui/material";
import { useTheme, alpha } from "@mui/material/styles";
import SpeedRoundedIcon from "@mui/icons-material/SpeedRounded";
import BuildRoundedIcon from "@mui/icons-material/BuildRounded";
import InsightsRoundedIcon from "@mui/icons-material/InsightsRounded";
import NotificationsRoundedIcon from "@mui/icons-material/NotificationsRounded";
import LogoutRoundedIcon from "@mui/icons-material/LogoutRounded";
import AddCars from "./AddCars";

export const SIDEBAR_WIDTH = 264;

const NAV_ITEMS = [
  { value: "maintenance", label: "Maintenance", icon: BuildRoundedIcon },
  { value: "insights", label: "Insights", icon: InsightsRoundedIcon },
  { value: "reminders", label: "Reminders", icon: NotificationsRoundedIcon },
];

/**
 * Persistent left nav on desktop (plain flex child, not an MUI Drawer --
 * avoids fighting MUI's permanent-drawer fixed-positioning internals for a
 * case a normal flex layout already handles). Becomes a temporary MUI
 * Drawer overlay below the md breakpoint.
 */
function Sidebar({
  user,
  cars,
  selectedCar,
  onSelectCar,
  onCarAdded,
  onDeleteCarRequest,
  activeTab,
  onTabChange,
  mode,
  onToggleMode,
  onLogout,
  mobileOpen,
  onCloseMobile,
}) {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up("md"));

  const content = (
    <Box
      sx={{
        width: SIDEBAR_WIDTH,
        height: "100%",
        display: "flex",
        flexDirection: "column",
        borderRight: "1px solid",
        borderColor: "divider",
        px: 2.5,
        py: 3,
      }}
    >
      {/* Brand */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, mb: 3 }}>
        <Box
          sx={{
            width: 34,
            height: 34,
            borderRadius: 1.5,
            display: "grid",
            placeItems: "center",
            backgroundColor: alpha(theme.palette.primary.main, 0.14),
            flexShrink: 0,
          }}
        >
          <SpeedRoundedIcon sx={{ color: "primary.main", fontSize: 20 }} />
        </Box>
        <Typography sx={{ fontWeight: 800, fontSize: 17 }}>AutoLogg</Typography>
      </Box>

      {/* Vehicle switcher */}
      <FormControl size="small" fullWidth sx={{ mb: 0.75 }}>
        <InputLabel>Vehicle</InputLabel>
        <Select
          label="Vehicle"
          value={selectedCar?.id ?? ""}
          onChange={(e) => {
            const carId = Number(e.target.value);
            const car = cars.find((c) => c.id === carId) || null;
            onSelectCar(car);
          }}
        >
          {cars.map((car) => (
            <MenuItem key={car.id} value={car.id}>
              {car.nickname
                ? `${car.nickname} (${car.year} ${car.make} ${car.model})`
                : `${car.year} ${car.make} ${car.model}`}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2.5 }}>
        <AddCars user={user} onCarAdded={onCarAdded} />
        <Button size="small" color="error" disabled={!selectedCar} onClick={onDeleteCarRequest} sx={{ fontSize: 13 }}>
          Delete car
        </Button>
      </Box>

      <Divider sx={{ mb: 1.5 }} />

      {/* Nav */}
      <List sx={{ py: 0 }}>
        {NAV_ITEMS.map((item) => {
          const Icon = item.icon;
          const active = activeTab === item.value;
          return (
            <ListItemButton
              key={item.value}
              selected={active}
              onClick={() => onTabChange(item.value)}
              sx={{
                mb: 0.5,
                borderLeft: "2px solid",
                borderLeftColor: active ? "primary.main" : "transparent",
                "&.Mui-selected": {
                  backgroundColor: alpha(theme.palette.primary.main, 0.12),
                  "&:hover": {
                    backgroundColor: alpha(theme.palette.primary.main, 0.16),
                  },
                },
              }}
            >
              <ListItemIcon sx={{ minWidth: 34, color: active ? "primary.main" : "text.secondary" }}>
                <Icon fontSize="small" />
              </ListItemIcon>
              <ListItemText
                primary={item.label}
                primaryTypographyProps={{
                  fontWeight: active ? 700 : 500,
                  color: active ? "text.primary" : "text.secondary",
                  fontSize: 14,
                }}
              />
            </ListItemButton>
          );
        })}
      </List>

      <Box sx={{ flex: 1 }} />

      {/* Footer */}
      <Divider sx={{ mb: 1.5 }} />
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1.5 }}>
        <Typography variant="body2" sx={{ color: "text.secondary" }}>
          {mode === "dark" ? "Dark" : "Light"}
        </Typography>
        <Switch size="small" checked={mode === "dark"} onChange={onToggleMode} color="primary" />
      </Box>
      <Typography
        variant="body2"
        sx={{ fontWeight: 600, mb: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
      >
        {user?.displayName || "Account"}
      </Typography>
      <Button
        fullWidth
        size="small"
        variant="outlined"
        color="error"
        startIcon={<LogoutRoundedIcon fontSize="small" />}
        onClick={onLogout}
      >
        Logout
      </Button>
    </Box>
  );

  if (isDesktop) {
    return content;
  }

  return (
    <Drawer
      variant="temporary"
      open={mobileOpen}
      onClose={onCloseMobile}
      ModalProps={{ keepMounted: true }}
      sx={{ "& .MuiDrawer-paper": { backgroundImage: "none" } }}
    >
      {content}
    </Drawer>
  );
}

export default Sidebar;
